import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { PropertyCard } from "@/components/PropertyCard";
import { PropertyFilters } from "@/components/PropertyFilters";
import { usePageContent } from "@/hooks/usePageContent";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Loader2, PlusCircle } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [seoData, setSeoData] = useState(null);
  const { get } = usePageContent("properties");

  useEffect(() => {
    fetchProperties(filters);
    fetchSeoData();
  }, []);

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${API}/seo/properties`);
      setSeoData(response.data);
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    }
  };

  const fetchProperties = async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });
      
      const url = params.toString() 
        ? `${API}/properties?${params.toString()}`
        : `${API}/properties`;
      
      const response = await axios.get(url);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchProperties(newFilters);
  };

  const getSpan = (index) => {
    const pattern = [6, 6, 4, 4, 4];
    return pattern[index % 5];
  };

  // Apply SEO tags via direct DOM manipulation
  useSEO(
    seoData ? {
      seo_title: seoData.seo_title || "Properties For Sale | SellLocalHomes",
      seo_description: seoData.seo_description || "Browse our complete collection of properties for sale.",
      seo_keywords: seoData.seo_keywords || "property listings, houses for sale, homes available",
      seo_robots: seoData.seo_robots || "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/properties"
    } : {
      seo_title: "Properties For Sale | SellLocalHomes",
      seo_description: "Browse our complete collection of properties for sale.",
      seo_keywords: "property listings, houses for sale, homes available",
      seo_robots: "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/properties"
    },
    { og_type: "website" }
  );

  return (
    <PageShell>
      <div className="min-h-screen" data-testid="properties-page">
      

      {/* Page Header with Inline Filters */}
      <section className="pt-12 md:pt-16 pb-8 bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 animate-fade-in-up">
            <div className="space-y-2">
              <h1 className="font-serif text-3xl md:text-4xl font-medium">
                {get("page_title", "All Properties")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {get("page_description", "Explore our carefully curated selection of exceptional homes.")}
              </p>
            </div>
            <div className="shrink-0">
              <PropertyFilters onFilterChange={handleFilterChange} initialFilters={filters} inline={true} />
            </div>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-4" data-testid="properties-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Results count */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-muted-foreground">
              {loading ? (
                "Searching..."
              ) : (
                <>
                  <span className="font-medium text-foreground">{properties.length}</span>
                  {" "}properties found
                </>
              )}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20 bg-muted/30 border border-border">
              <p className="text-muted-foreground text-lg mb-2">No properties found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your filters to see more results
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {properties.map((property, index) => (
                <PropertyCard
                  key={property.property_id}
                  property={property}
                  span={12}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Floating CTA - List Your Home */}
      <div className="fixed bottom-6 left-6 z-40 animate-fade-in-up">
        <Button
          onClick={() => navigate("/property-admin/login")}
          className="h-12 px-6 bg-primary text-white hover:bg-primary/90 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          data-testid="floating-list-house-btn"
        >
          <PlusCircle size={18} />
          <span className="font-medium">List Your Home</span>
        </Button>
      </div>
      </div>
    </PageShell>
  );
}